import MidiEffect from './MidiEffect.js';
import {bulkUpdate} from 'utils/bulkUpdate.js';
import {getTriggerTimes} from 'keyboard/TriggerTimeHelper.js';
import {setTrackWarp} from 'actions/index.js';
import Tone from 'tone';
import {isEmpty} from 'utils/object.js';
import Resolution from 'engine/Resolution.js';
import MidiTrigger from './MidiTrigger.js';
import {AbstractParameter} from 'parameters/AbstractParameter.js';
import OptionParameter from 'parameters/OptionParameter.js';

const MIN_ATTACK = 8; // 8 ms for "smoothness"
const MIN_RELEASE = 2;

const DEFAULT_LENGTH = 1000;

/**
 * Timestretchs a sample
 */
class WarpEffect extends MidiEffect {
    constructor(track, enableOverride=true) {
        super('Warp', track);

        window.warp = this;
        if (enableOverride) {
            this.enabledParameter = new AbstractParameter(
                'enabled', this, (o, x) => this.onEnable(x),
                {
                    min: false,
                    max: true,
                    unit: '',
                    defaultValue: true,
                    automatable: false
                });
            
            this.registerParameter(this.enabledParameter);
        }
  
        // how long the warp cycle goes for (in bars). effectively how
        // stretched it sounds
        this.warpLengthParameter = new AbstractParameter(
            'warpLength', this, (o, x) => [],
            {
                documentation: "Controls the tempo of the sample in proportion to the project tempo. A higher number makes the sample slower, and more time-stretched.",
                defaultValue: 1,
                min: 0.05,
                max: 16,
                unit: 'bars',
                prettyName: 'Warp Length'
            });

        this.registerParameter(this.warpLengthParameter);

        this.warpFactorParameter = new AbstractParameter(
            'warpFactor', this, (o, x) => [],
            {
                documentation: "Multiples the Warp Speed by this whole number. A quick way to double or half the sample tempo",
                defaultValue: 2,
                min: 1,
                max: 8,
                unit: '',
                prettyName: 'Warp Factor'
            });

        this.registerParameter(this.warpFactorParameter);

        this.glideParameter = new AbstractParameter(
            'glide', this, (o, x) => [],
            {
                defaultValue: 1,
                min: 0,
                max: 4,
                unit: '',
                automatable: false,
            });

        this.registerParameter(this.glideParameter);

        // how long the warp cycle goes for (in bars). effectively how
        // long it actually sounds
        this.resolutionParameter = new OptionParameter(
            'resolution', this, (o, x) => x,
            {
                documentation: "The rate at which we trigger each warped grain.",
                min: 0,
                max: 1,
                unit: '', 
                prettyName: "Rate",
                options: {
                    0: new Resolution(4, false),
                    0.1: new Resolution(4, true),
                    0.2: new Resolution(8, false),
                    0.3: new Resolution(8, true),
                    0.4: new Resolution(16, false),
                    0.5: new Resolution(16, true),
                    0.6: new Resolution(32, false),
                    0.7: new Resolution(32, true),
                },
                defaultValue: new Resolution(16, false)
            }
        );

        this.registerParameter(this.resolutionParameter);

        this.loopLengthParameter =  new AbstractParameter(
            'loopLength', this, (o, x) => [],
            {
                documentation: "The % of the sample-loop we will go through before looping back.",
                defaultValue: 100,
                min: 0,
                max: 100,
                unit: '%',
                prettyName: "Loop Length"
            });

        this.registerParameter(this.loopLengthParameter);

        this.durationFactorParameter =  new AbstractParameter(
            'durationFactor', this, (o, x) => [],
            {
                documentation: "The duration of each grain to be triggered (according to the warp-rate).",
                defaultValue: 1,
                min: 0,
                max: 2,
                unit: '',
                prettyName: "Grain Size"
            });

        this.registerParameter(this.durationFactorParameter);

        this.smoothnessParameter = new AbstractParameter(
            'smoothness', this, (o, x) => [],
            {
                documentation: "Controls the attack/release of each grain, smoothing them out. Higher numbers mean smoother transitions (higher attack/release)",
                defaultValue: 1,
                min: 0.1,
                max: 20,
                unit: '',
                prettyName: "Smoothness"
            });

        this.registerParameter(this.smoothnessParameter);

        this.sliceRetriggerParameter = new AbstractParameter(
            'sliceRetrigger', this, (o, x) => [],
            {
                defaultValue: true,
                min: false,
                max: true,
                unit: '',
                automatable: false,
            });

        this.registerParameter(this.sliceRetriggerParameter);

        this.noteToDuration = {};
        this.noteToTrigger = {};
        this.stepNumberTriggered = 0;
        this.notes = {};
        this.times = [];
    }

    get documentation() {
        return [
            `Syncs a sample to the project tempo, dividing the 
sample into many pieces (according to the warp-rate), and triggering them at this rate.`,
            "Track needs to be playing to work."];
    }

    getVisualizedParameters() {
        return [this.resolutionParameter, ... super.getVisualizedParameters()];
    }

    getText() {
        return this.resolutionParameter.get().toString();
    }

    onEnable(enabled) {
        let s = this.track.sequencerEngine.store.getState();
        let w = s.tracks.trackWarps[this.track.subSequence.id] || {};
        if (w[this.track.trackNumber] !== enabled) {
            this.track.sequencerEngine.store.dispatch(setTrackWarp(
                this.track.subSequence.id,
                this.track.trackNumber,
                enabled));
        }
    }

    releaseAll(note) {
        this.notes = {};
        this.noteToTrigger = {};
        this.noteToDuration = {};
    }

    isNothingPlaying() {
        return (isEmpty(this.noteToTrigger) && isEmpty(this.notes));
    }

    input(trigger, isLive=false) {
        super.input(trigger, isLive);
        
        let stepNumber = trigger.stepNumber;
        let note = trigger.stepData.transpose;
        let slice = trigger.stepData.slice;
        let time = trigger.time;

        // need to know when the first trigger happened
        if (this.isNothingPlaying() && this.sliceRetrigger) {
            this.stepNumberTriggered = undefined;
        }

        // this happens when addNote is called (i.e. when live
        // triggering)
       if (isLive) {
           // when "live triggered" we keep track of the triggers
           // in this.notes (b/c there are no durations to keep track of)
           if (!this.gated) { // ungated
                // reset the duration elapsed since we want to
                // retrigger
               if (this.isNothingPlaying() || stepNumber == 0 || this.sliceRetrigger) {
                   this.stepNumberTriggered = stepNumber;
               }
               
                // "release" all the other slices (i.e. just
                // use this slice from now on)
               this.releaseAll();
               this.notes[note] = [{slice, time}];
            } else {
                let slices = this.notes[note] || [];

                // keep track of timing
                slices.push({slice, time});
                this.notes[note] = slices;
            }
            return;
        }

        let duration = trigger.stepData.duration * 2;

        if (!this.gated || (this.track.rolling && this.sequencerEngine.isSequenceRolling)) {
            this.clear(false);
            if (this.sliceRetrigger || stepNumber === 0) {
                this.stepNumberTriggered = stepNumber;
            }
        }

        if (this.noteToDuration[note] === undefined) {
            this.noteToDuration[note] = 0;
        }

        this.noteToDuration[note] = duration;
        this.noteToTrigger[note] = trigger;
    }

    getLastTime(time) {
        for (let i=this.times.length - 1; i >= 0; i--) {
            let _time = this.times[i];
            if (_time < time) {
                return _time;
            }
        }
        return this.times[this.times.length - 1];
    }

    getDurationElapsed(stepNumber) {
        let durationElapsed = stepNumber - this.stepNumberTriggered;
        if (durationElapsed < 0) {
            durationElapsed = durationElapsed + this.track.length;
        }
        return durationElapsed;
    }

    output(time=Tone.Master.context.currentTime, stepNumber, actualStepNumber, isLive=false, steps) {
        let triggers = [];

        if (!isLive) {
            this.times.push(time);
        }

        this.currentStep = stepNumber;

        let durationElapsed = this.getDurationElapsed(stepNumber);
        durationElapsed = isNaN(durationElapsed) ? 0 : durationElapsed;
        let triggerTimes = getTriggerTimes(
            durationElapsed, this.resolution);

        // first go through this.noteToTrigger which keeps track
        // of sequenced triggers (i.e. already recorded steps w/ durations)
        for (let note in this.noteToTrigger) {
            let trigger = this.noteToTrigger[note];
            if (trigger.offset === undefined) {
                trigger.offset = trigger.time - time;
            }
            for (let i in triggerTimes) {
                this.getTrigger(
                    trigger.stepData,
                    note,
                    time + triggerTimes[i].time + trigger.offset,
                    stepNumber,
                    actualStepNumber,
                    trigger.stepData.slice)
                    .forEach(trigger => {
                        triggers.push(trigger);
                    });
            }
        }

        // then we go through this.notes which keeps track
        // of live triggers (i.e. triggers w/ durations)
        for (let note in this.notes) {
            let slices = this.notes[note];
            for (let j in slices) {
                let {slice, offset} = slices[j];
                if (offset === undefined) {
                    let lastTime = this.getLastTime(time); 
                    offset = time - lastTime;
                    slices[j].offset = offset;
                }

                if (isLive) {

                    for (let step of steps) {
                        let stepOffset = step - stepNumber;
                        let _triggerTimes = getTriggerTimes(
                            stepOffset, this.resolution);
                        let time32N = stepOffset * (new Tone.Time('32n').toMilliseconds() / 1000);
                        _triggerTimes.forEach(
                            triggerTime => 
                                this.getTrigger(
                                    this.track.trackData,
                                    note,
                                    stepOffset === 0 ? undefined : time + time32N + triggerTime.time,
                                    step,
                                    step,
                                    slice)
                                .forEach(trigger => {
                                    if (this.sampler && stepOffset === 0 && !this.gated) {
                                        this.sampler.releaseAll(Tone.Master.context.currentTime);
                                    }
                                    this.lastTriggerTime = trigger.time;
                                    triggers.push(trigger);
                                }));
                        
                    }
                } else {
                    for (let i in triggerTimes) {
                        this.getTrigger(
                            this.track.trackData,
                            note,
                            time + triggerTimes[i].time + offset,
                            stepNumber,
                            actualStepNumber,
                            slice)
                            .forEach(trigger => {
                                let diff = trigger.time - this.lastTriggerTime;
                                this.lastTriggerTime = trigger.time;
                                trigger.stepData.track = this.track;
                                triggers.push(trigger);
                            });
                }
                }
            }
        }

        // update the duration data structures
        for (let i in this.noteToDuration) {
            if (!this.gated) {
                continue;
            }
            this.noteToDuration[i] = Math.max(
                0, this.noteToDuration[i] - 1);

            if (this.noteToDuration[i] <= 0) {
                if (this.track.rolling && this.sequencerEngine.isSequenceRolling) {
                } else {
                    // erase the step data
                    delete this.noteToTrigger[i];
                }
            }
        }

        return triggers;
    }

    addNote(transpose, sliceNumber=0, stepNumber=0, time) {
        let stepData = this.getStep(this.track.trackData, transpose, null, false);
        stepData.velocity = this.track.trackData.velocity;
        stepData.duration = null;
        stepData.transpose = transpose;
        stepData.slice = sliceNumber;

        this.input(new MidiTrigger(stepData, time, stepNumber + 1), true);
    }

    removeNote(note, sliceNumber, skip=!this.gated) {
        if (skip) {
            return;
        }
        if (this.notes[note] !== undefined) {
            let _slices = this.notes[note];
            let slices = this.notes[note].map(x => x.slice);
            let i = slices.indexOf(sliceNumber);
            _slices = _slices.filter(x => x.slice !== sliceNumber);
            this.notes[note] = _slices;

            if (this.notes[note].length === 0) {
                delete this.notes[note];
            }
        }
    }

    getStepPercentage(stepNumber, sliceNumber) {
        let durationElapsed = this.getDurationElapsed(stepNumber);
        // durationElapsed = isNaN(durationElapsed) ? 0 : durationElapsed;

        let warpLengthInSteps = 32 * this.getSliceWarpLength(sliceNumber); 
        let warpProduct = this.warpLength * this.warpFactor;
        return (100 * durationElapsed / warpLengthInSteps) / (warpProduct);
    }

    getSliceWarpLength() {
        let sampleBpm = this.track.tempo;
        let projectBpm = this.track.sequencerEngine.bpm;

        if (sampleBpm === undefined || sampleBpm === null || sampleBpm === 0) {
            return 1;
        }

        let ratio = sampleBpm / projectBpm;
        return ratio;
    }

    getSliceLoopLength(note, sliceNumber) {
        let sampler = undefined;
        if (this.track.isFrozen) {
            for (let col of this.track.frozenColumns) {
                if (col.frozenSamplers[col.playing]) {
                    sampler = col.frozenSamplers[col.playing];
                }
            }
        } else if (this.track.instrument && this.track.instrument.getSampler) {
            let s = this.track.instrument.getSampler(note);
            if (s.samplers) {
                sampler = s.samplers[0];
            }
        } else {
            sampler = this.track.getSamplers()[0];
        }

        if (sampler === undefined) {
            return 8;
        }
        let slice = sampler.getSlice(sliceNumber);
        let sampleLength = slice.endTime - slice.startTime;
        let sampleLoopTime = new Tone.Time('32n').toMilliseconds() * 64 / 1000.0;
        let l = 100 * sampleLoopTime / sampleLength;
        return l;
    }

    getTrigger(toCopy, note, time, stepNumber, actualStepNumber, sliceNumber) {
        if (this.stepNumberTriggered === undefined) {
            if (this.gated || this.sliceRetrigger || this.isNothingPlaying()) {
                this.stepNumberTriggered = stepNumber;
            }
        }
        note = parseFloat(note);
        let stepPercentage = this.getStepPercentage(
            stepNumber,
            sliceNumber);
        let duration = this.gated && toCopy.duration < 1 ? toCopy.duration : 1;
        let stepData = this.getStep(toCopy, note, duration, true);
        stepData.start = (stepPercentage * this.getSliceLoopLength(note, sliceNumber) / 100) % this.loopLength;
        stepData.slice = sliceNumber;
        stepData.duration *= this.durationFactor;
        stepData.attack += MIN_ATTACK * this.smoothness;
        if (stepPercentage === 0) {
            stepData.attack /= 2;
        }  else {
            stepData.attack *= 2;
        }
        if (this.track.instrument === undefined) {
            stepData.release += MIN_RELEASE * this.smoothness;
        }
        return [new MidiTrigger(stepData, time, actualStepNumber)];
    }

    get sampler() {
        return this.track.getSamplers()[0];
    }

    get gated() {
        return this.track.isFrozen ? false : this.track.gated;
    }

    // need to call addNote when live triggering
    isRealTime() {
        return true;
    }

    onNewStep() {}
    
    clear(clearStepNumberTriggered=true) {
        this.noteToDuration = {};
        this.noteToTrigger = {};
        if (clearStepNumberTriggered) {
            this.stepNumberTriggered = undefined;
        }
        this.notes = {};
        this.times = [];
    }

    get durationFactor() {
        return this.durationFactorParameter.get();
    }

    set durationFactor(durationFactor) {
        return this.durationFactorParameter.set(durationFactor);
    }

    get glide() {
        return this.glideParameter.get();
    }

    set glide(glide) {
        return this.glideParameter.set(glide);
    }

    get warpFactor() {
        return this.warpFactorParameter.get();
    }

    set warpFactor(warpFactor) {
        return this.warpFactorParameter.set(warpFactor);
    }

    get warpLength() {
        return this.warpLengthParameter.get();
    }

    set warpLength(w) {
        return this.warpLengthParameter.set(w);
    }

    get loopLength() {
        return this.loopLengthParameter.get();
    }

    set loopLength(l) {
        return this.loopLengthParameter.set(l);
    }

    get smoothness() {
        return this.smoothnessParameter.get();
    }

    set smoothness(s) {
        return this.smoothnessParameter.set(s);
    }

    get resolution() {
        return new Resolution().fromJson(this.resolutionParameter.get());
    }

    set resolution(r) {
        return this.resolutionParameter.set(r);
    }

    get sliceRetrigger() {
        return this.sliceRetriggerParameter.get();
    }

    set sliceRetrigger(s) {
        return this.sliceRetriggerParameter.set(s);
    }

    getNextTriggerStep(stepNumber) {
        let resolution = this.resolution.resolution;
        let divider = 32 / resolution;
        let remainder = stepNumber % divider;
        return stepNumber + (divider - remainder);
    }

    getCopy(track, ignoreId=true) {
        let warpEffect = new WarpEffect(track);
        warpEffect.fromJson(this.getJson(), ignoreId);
        return warpEffect;
    }

    fromJson(json, isCopy) {
        let bulk = super.fromJson(json, isCopy);
        bulkUpdate(
            this.resolutionParameter,
            new Resolution(16, false)
                .fromJson(json.resolution),
            bulk);
        return bulk;
    }
}

export default WarpEffect;
